import config from "../../../config/config";
import { authFetch, buildAuthHeaders } from "../../../public/api/httpClient";

const getInwardBase = () => {
  return `${config.BASE_URL}/inward-register`;
};

const getOutwardBase = () => {
  return `${config.BASE_URL}/outward-register`;
};

async function parseJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || "Invalid response" };
  }
}

function messageForResponse(res, payload, fallback) {
  if (res.status >= 500) {
    return "Internal server error. Please try again later.";
  }
  const m = payload?.message;
  return typeof m === "string" && m.trim() ? m : fallback;
}

/** Staff picker for received_by / dispatched_by (inward-outward VIEW only). */
export const searchUsers = async (query) => {
  const params = new URLSearchParams();
  if (query) params.append("q", String(query).trim());
  const res = await authFetch(`${getInwardBase()}/users/search?${params.toString()}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(messageForResponse(res, data, "Error searching users"));
  }
  return data;
};

// ------------------------
// Dispatch Modes
// ------------------------

export const fetchDispatchModes = async ({ scope } = {}) => {
  const qs =
    scope === "master"
      ? "?scope=master"
      : scope === "dropdown"
        ? "?scope=dropdown"
        : "?scope=dropdown";
  const res = await authFetch(`${getInwardBase()}/dispatch-modes${qs}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(messageForResponse(res, data, "Failed to fetch dispatch modes"));
  }
  return data;
};

export const createDispatchMode = async (data, csrfToken) => {
  try {
    const res = await authFetch(`${getInwardBase()}/dispatch-modes`, {
      method: "POST",
      credentials: "include",
      headers: buildAuthHeaders(
        csrfToken ? { "X-CSRF-Token": csrfToken } : {},
      ),
      body: JSON.stringify(data),
    });
    const payload = await parseJson(res);
    if (!res.ok) {
      return {
        data: null,
        error: {
          message: messageForResponse(
            res,
            payload,
            "Failed to create dispatch mode",
          ),
          details: res.status >= 500 ? undefined : payload.details,
        },
      };
    }
    return { data: payload, error: null };
  } catch (error) {
    return {
      data: null,
      error: {
        message:
          typeof error?.message === "string"
            ? error.message
            : "Failed to create dispatch mode",
      },
    };
  }
};

export const updateDispatchMode = async (id, data, csrfToken) => {
  try {
    const res = await authFetch(
      `${getInwardBase()}/dispatch-modes/${encodeURIComponent(String(id))}`,
      {
        method: "PUT",
        credentials: "include",
        headers: buildAuthHeaders(
          csrfToken ? { "X-CSRF-Token": csrfToken } : {},
        ),
        body: JSON.stringify(data),
      },
    );
    const payload = await parseJson(res);
    if (!res.ok) {
      return {
        data: null,
        error: {
          message: messageForResponse(
            res,
            payload,
            "Failed to update dispatch mode",
          ),
          details: res.status >= 500 ? undefined : payload.details,
        },
      };
    }
    return { data: payload, error: null };
  } catch (error) {
    return {
      data: null,
      error: {
        message:
          typeof error?.message === "string"
            ? error.message
            : "Failed to update dispatch mode",
      },
    };
  }
};

export const deleteDispatchMode = async (id, csrfToken) => {
  const res = await authFetch(
    `${getInwardBase()}/dispatch-modes/${encodeURIComponent(String(id))}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: buildAuthHeaders(
        csrfToken ? { "X-CSRF-Token": csrfToken } : {},
      ),
    },
  );
  const payload = await parseJson(res);
  if (!res.ok) {
    throw new Error(
      messageForResponse(res, payload, "Failed to delete dispatch mode"),
    );
  }
  return payload;
};

// ------------------------
// Document Types
// ------------------------

export const fetchDocumentTypes = async ({ scope } = {}) => {
  const qs =
    scope === "master"
      ? "?scope=master"
      : scope === "dropdown"
        ? "?scope=dropdown"
        : "?scope=dropdown";
  const res = await authFetch(`${getInwardBase()}/document-types${qs}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(messageForResponse(res, data, "Failed to fetch document types"));
  }
  return data;
};

export const createDocumentType = async (data, csrfToken) => {
  try {
    const res = await authFetch(`${getInwardBase()}/document-types`, {
      method: "POST",
      credentials: "include",
      headers: buildAuthHeaders(
        csrfToken ? { "X-CSRF-Token": csrfToken } : {},
      ),
      body: JSON.stringify(data),
    });
    const payload = await parseJson(res);
    if (!res.ok) {
      return {
        data: null,
        error: {
          message: messageForResponse(
            res,
            payload,
            "Failed to create document type",
          ),
          details: res.status >= 500 ? undefined : payload.details,
        },
      };
    }
    return { data: payload, error: null };
  } catch (error) {
    return {
      data: null,
      error: {
        message:
          typeof error?.message === "string"
            ? error.message
            : "Failed to create document type",
      },
    };
  }
};

export const updateDocumentType = async (id, data, csrfToken) => {
  try {
    const res = await authFetch(
      `${getInwardBase()}/document-types/${encodeURIComponent(String(id))}`,
      {
        method: "PUT",
        credentials: "include",
        headers: buildAuthHeaders(
          csrfToken ? { "X-CSRF-Token": csrfToken } : {},
        ),
        body: JSON.stringify(data),
      },
    );
    const payload = await parseJson(res);
    if (!res.ok) {
      return {
        data: null,
        error: {
          message: messageForResponse(
            res,
            payload,
            "Failed to update document type",
          ),
          details: res.status >= 500 ? undefined : payload.details,
        },
      };
    }
    return { data: payload, error: null };
  } catch (error) {
    return {
      data: null,
      error: {
        message:
          typeof error?.message === "string"
            ? error.message
            : "Failed to update document type",
      },
    };
  }
};

export const deleteDocumentType = async (id, csrfToken) => {
  const res = await authFetch(
    `${getInwardBase()}/document-types/${encodeURIComponent(String(id))}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: buildAuthHeaders(
        csrfToken ? { "X-CSRF-Token": csrfToken } : {},
      ),
    },
  );
  const payload = await parseJson(res);
  if (!res.ok) {
    throw new Error(
      messageForResponse(res, payload, "Failed to delete document type"),
    );
  }
  return payload;
};

// ------------------------
// Inward Register
// ------------------------

export const createInward = async (data, csrfToken) => {
  try {
    const isFormData = data instanceof FormData;
    const headers = buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {});
    
    if (isFormData) {
      delete headers["Content-Type"];
    }

    const res = await authFetch(`${getInwardBase()}/inwardEntry`, {
      method: "POST",
      credentials: "include",
      headers,
      body: isFormData ? data : JSON.stringify(data),
    });
    const payload = await parseJson(res);
    if (!res.ok) {
      const backendError =
        typeof payload?.error === "string" && payload.error.trim()
          ? payload.error
          : undefined;
      return {
        data: null,
        error: {
          error: backendError,
          message: messageForResponse(
            res,
            payload,
            "Failed to create inward entry",
          ),
          details: res.status >= 500 ? undefined : payload.details,
        },
      };
    }
    return { data: payload, error: null };
  } catch (error) {
    return {
      data: null,
      error: {
        message:
          typeof error?.message === "string"
            ? error.message
            : "Failed to create inward entry",
      },
    };
  }
};

// ------------------------
// Outward Register
// ------------------------

export const createOutward = async (data, csrfToken) => {
  try {
    const isFormData = data instanceof FormData;
    const headers = buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {});
    
    if (isFormData) {
      delete headers["Content-Type"];
    }

    const res = await authFetch(`${getOutwardBase()}/outwardEntry`, {
      method: "POST",
      credentials: "include",
      headers,
      body: isFormData ? data : JSON.stringify(data),
    });
    const payload = await parseJson(res);
    if (!res.ok) {
      const backendError =
        typeof payload?.error === "string" && payload.error.trim()
          ? payload.error
          : undefined;
      return {
        data: null,
        error: {
          error: backendError,
          message: messageForResponse(
            res,
            payload,
            "Failed to create outward entry",
          ),
          details: res.status >= 500 ? undefined : payload.details,
        },
      };
    }
    return { data: payload, error: null };
  } catch (error) {
    return {
      data: null,
      error: {
        message:
          typeof error?.message === "string"
            ? error.message
            : "Failed to create outward entry",
      },
    };
  }
};

const inwardOutwardScopeQuery = (scope) =>
  scope === "control" ? "?scope=control" : "";

export const fetchInwards = async ({ scope } = {}) => {
  const res = await authFetch(`${getInwardBase()}/inwardEntry${inwardOutwardScopeQuery(scope)}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(messageForResponse(res, data, "Failed to fetch inward entries"));
  }
  return data;
};

export const updateInward = async (id, data, csrfToken, { scope } = {}) => {
  try {
    const isFormData = data instanceof FormData;
    const headers = buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {});
    
    if (isFormData) {
      delete headers["Content-Type"];
    }

    const res = await authFetch(
      `${getInwardBase()}/inwardEntry/${encodeURIComponent(String(id))}${inwardOutwardScopeQuery(scope)}`,
      {
      method: "PUT",
      credentials: "include",
      headers,
        body: isFormData ? data : JSON.stringify(data),
      },
    );
    const payload = await parseJson(res);
    if (!res.ok) {
      const backendError =
        typeof payload?.error === "string" && payload.error.trim()
          ? payload.error
          : undefined;
      return {
        data: null,
        error: {
          error: backendError,
          message: messageForResponse(
            res,
            payload,
            "Failed to update inward entry",
          ),
          details: res.status >= 500 ? undefined : payload.details,
        },
      };
    }
    return { data: payload, error: null };
  } catch (error) {
    return {
      data: null,
      error: {
        message:
          typeof error?.message === "string"
            ? error.message
            : "Failed to update inward entry",
      },
    };
  }
};

export const deleteInward = async (id, csrfToken, { scope } = {}) => {
  const res = await authFetch(
    `${getInwardBase()}/inwardEntry/${encodeURIComponent(String(id))}${inwardOutwardScopeQuery(scope)}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: buildAuthHeaders(
        csrfToken ? { "X-CSRF-Token": csrfToken } : {},
      ),
    },
  );
  const payload = await parseJson(res);
  if (!res.ok) {
    throw new Error(
      messageForResponse(res, payload, "Failed to delete inward entry"),
    );
  }
  return payload;
};

export const fetchOutwards = async ({ scope } = {}) => {
  const res = await authFetch(`${getOutwardBase()}/outwardEntry${inwardOutwardScopeQuery(scope)}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(messageForResponse(res, data, "Failed to fetch outward entries"));
  }
  return data;
};

export const updateOutward = async (id, data, csrfToken, { scope } = {}) => {
  try {
    const isFormData = data instanceof FormData;
    const headers = buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {});
    
    if (isFormData) {
      delete headers["Content-Type"];
    }

    const res = await authFetch(
      `${getOutwardBase()}/outwardEntry/${encodeURIComponent(String(id))}${inwardOutwardScopeQuery(scope)}`,
      {
      method: "PUT",
      credentials: "include",
      headers,
        body: isFormData ? data : JSON.stringify(data),
      },
    );
    const payload = await parseJson(res);
    if (!res.ok) {
      const backendError =
        typeof payload?.error === "string" && payload.error.trim()
          ? payload.error
          : undefined;
      return {
        data: null,
        error: {
          error: backendError,
          message: messageForResponse(
            res,
            payload,
            "Failed to update outward entry",
          ),
          details: res.status >= 500 ? undefined : payload.details,
        },
      };
    }
    return { data: payload, error: null };
  } catch (error) {
    return {
      data: null,
      error: {
        message:
          typeof error?.message === "string"
            ? error.message
            : "Failed to update outward entry",
      },
    };
  }
};

export const deleteOutward = async (id, csrfToken, { scope } = {}) => {
  const res = await authFetch(
    `${getOutwardBase()}/outwardEntry/${encodeURIComponent(String(id))}${inwardOutwardScopeQuery(scope)}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: buildAuthHeaders(
        csrfToken ? { "X-CSRF-Token": csrfToken } : {},
      ),
    },
  );
  const payload = await parseJson(res);
  if (!res.ok) {
    throw new Error(
      messageForResponse(res, payload, "Failed to delete outward entry"),
    );
  }
  return payload;
};

